<template>
  <div :class="['page', $style.playGamePage]">
    <template v-if="this.game && this.user && !isLoading && !game.phaseLoading && viewReady">
      <GameScreen
        v-model:tab="tabSelected"
        v-model:winnerModal="showWinnerModal"
        v-model:roleModal="showPlayerRoleModal"
        :gameWinner="gameWinner"
        :playerRoleReady="playerRoleReady"
        :canControlPhase="canControlPhase"
        :chatReadOnly="isChatReadOnly"
        :prelimJustSubmitted="justSubmittedPrelim"
        :saboteurJustSubmitted="justSubmittedSaboteur"
        :scoresLoaded="scoresLoadedForReveal"
        :pendingModerators="pendingModerators"
        :sendMessage="sendMessage"
        :updateTextVote="updateTextVote"
        :submitPrelimVote="submitPrelimVote"
        :submitVote="submitVote"
        :proceedNext="advanceToNextPhase"
        @update:pendingModerators="pendingModerators = $event"
        @update:groups="initialGroups = $event"
        @start-game="startGame"
        @continue-game="continueGame"
        @end-game="endGame"
        @timer-ended="onTimerEnd"
      />
    </template>

    <div
      v-if="isLoading || game?.phaseLoading || !viewReady"
      :class="['loader-container']"
    >
      <img
        :src="canUseHostView ? hostLoaderGif : playerLoaderGif"
        alt="loader-gif"
        class="loader-gif"
      />
      <!-- <div :class="['loader']">Loading ...</div> -->
    </div>
     <div v-if="isReconnectFlow && isRehydrating" :class="['reloader-container']">
      <img :src="appLoaderGif " alt="loader-gif" class="loader-gif"/>
      <div :class="$style.reconnectLoaderText">Reconnecting...</div>
    </div>
    <template v-if="showMissingVotesModal && isHost">
      <template v-if="elementViewPhases.gamePrelimVoteBox.includes(game.phase)">
        <MissingVotesModal
          :groups="groupsWithPrelimVoteCastedPlayers"
          :closeModal="closeMissingVotesModal"
          :proceedNext="advanceToNextPhase"
        />
      </template>

      <template v-if="elementViewPhases.gameTextsVoteBox.includes(game.phase)">
        <MissingVotesModal
          :groups="groupsWithTextVoteCastedPlayers"
          :closeModal="closeMissingVotesModal"
          :proceedNext="advanceToNextPhase"
        />
      </template>

      <template v-if="elementViewPhases.gameVoteBox.includes(game.phase)">
        <MissingVotesModal
          :groups="groupsWithSaboteurVoteCastedPlayers"
          :closeModal="closeMissingVotesModal"
          :proceedNext="advanceToNextPhase"
        />
      </template>
    </template>

    <template v-if="isHost">
      <UtilConfirmationModal
        :showModal="showStartGameConfirmationModal"
        :closeModal="closeStartGameConfirmationModal"
        :title="$t('start-game-confirmation')"
        :secondaryText="$t('start-game-desc')"
        :primaryAction="handleStartGame"
        :secondaryAction="closeStartGameConfirmationModal"
        :primaryActionText="$t('start')"
        :secondaryActionText="$t('cancel')"
        :helperText="$t('start-game-helper')"
        :bgClickClose="false"
      />
    </template>
    <template v-if="isHost">
      <UtilConfirmationModal
        :showModal="showEndGameConfirmationModal"
        :closeModal="closeEndGameConfirmationModal"
        :title="$t('end-game-confirmation')"
        :secondaryText="$t('end-game-desc')"
        :primaryAction="confirmEndGame"
        :secondaryAction="closeEndGameConfirmationModal"
        :primaryActionText="$t('end-game')"
        :secondaryActionText="$t('cancel')"
        :helperText="$t('end-game-helper')"
        :bgClickClose="false"
      />
    </template>
    <template v-if="showFullVotesReceivedModal && isHost">
      <UtilConfirmationModal
        :showModal="showFullVotesReceivedModal"
        :closeModal="closeFullVotesReceivedModal"
        :title="
          game.phase === 'PRELIMINARY_VOTE'
            ? $t('votes-complete-prelim')
            : game.phase === 'DISCUSS'
            ? $t('votes-complete-text')
            : $t('votes-complete-saboteur')
        "
        :secondaryText="$t('auto-proceed-helper')"
        :primaryAction="
          () => {
            closeFullVotesReceivedModal();
            advanceToNextPhase();
          }
        "
        :secondaryAction="closeFullVotesReceivedModal"
        :primaryActionText="$t('proceed')"
        :secondaryActionText="$t('wait')"
        :helperText="$t('auto-proceed-helper')"
        :bgClickClose="false"
      />
    </template>

    <template v-if="showConsensusModal">
      <UtilConfirmationModal
        :showModal="showConsensusModal"
        :closeModal="closeConsensusModal"
        :title="$t('consensus-reached')"
        :secondaryText="$t('proceed-next-phase')"
        :primaryAction="proceedWithConsensus"
        :secondaryAction="closeConsensusModal"
        :primaryActionText="$t('proceed')"
        :secondaryActionText="$t('wait')"
        :helperText="$t('auto-proceed-helper')"
        :bgClickClose="false"
      />
    </template>
  </div>
</template>

<script>
import { useGameStore } from "@/stores/gameStore";
import styles from "@/css/PlayGameView.module.css";
import { Filter } from "bad-words";
import HostLoaderGif from "@/assets/gifs/host-loader.svg";
import PlayerLoaderGif from "@/assets/gifs/player-loader.svg";
import { toast } from "vue3-toastify";
import gameService from "@/services/game.service";
import { fireDB } from "@/utils/firebase";
import { storeToRefs } from "pinia";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import UtilConfirmationModal from "@/components/util/UtilConfirmationModal.vue";
import { useGameGroupStore } from "@/stores/game/gameGroupStore";
import MissingVotesModal from "@/components/playGame/MissingVotesModal.vue";
import AppLoaderGif from "@/assets/gifs/app-loader.svg";
import GameScreen from "@/components/playGame/GameScreen.vue";
import useGameViewer from "@/utils/useGameViewer";
import { ELEMENT_VIEW_PHASES } from "@/utils/gameScreenLayout";

export default {
  components: {
    GameScreen,
    UtilConfirmationModal,
    MissingVotesModal,
  },
  name: "PlayGameView",
  props: {
      host: {
        type: Object,
        default: null
      }
  },
  data() {
    return {
      scoresLoadedForReveal: false,
      appLoaderGif: AppLoaderGif,
      isRehydrating: false,
      playersReady: false,
      isReconnectFlow: false,
      badWordsFilter: new Filter(),
      hostLoaderGif: HostLoaderGif,
      playerLoaderGif: PlayerLoaderGif,
      pendingModerators: {}, // { groupId: playerId }
      // detectiveHatIcon: DetectiveHatIcon,
      // forwardArrowIcon: ForwardArrowIcon,
      // userIcon: UserIcon,
      // comedyIcon: ComedyIcon,
       hostHydrated: false,
      isLoading: true,
      justSubmittedPrelim: false,
      justSubmittedSaboteur: false,
      timerExpiredPhase: null,
      // phaseTimerDuration: 120,
      tabSelected: "gameTexts",
      hasShownRoleModal: false,
      showMissingVotesModal: false,
      fbPlayersSnapshot: null,
      fbGameSnapshot: null,
      fbMessagesSnapshot: null,
      fbAllChatsUnsubs: {},
      showWinnerModal: false,
      gameWinner: "",
      showPlayerRoleModal: false,
      playerRoleReady: false,
      showStartGameConfirmationModal: false,
      showEndGameConfirmationModal: false,
      initialGroups: [],
      showConsensusModal: false,
      showFullVotesReceivedModal: false,
    };
  },
  setup() {
    const gameStore = useGameStore();
    const { game, isHost, user, isSaboteur } = storeToRefs(gameStore);

    const gameGroupStore = useGameGroupStore();
    const {
      groups,
      groupsWithSaboteurVoteCastedPlayers,
      groupsWithPrelimVoteCastedPlayers,
      groupsWithTextVoteCastedPlayers,
      myGroup,
    } = storeToRefs(gameGroupStore);

    return {
      gameStore,
      game,
      isHost,
      user,
      isSaboteur,
      gameGroupStore,
      groups,
      groupsWithSaboteurVoteCastedPlayers,
      groupsWithPrelimVoteCastedPlayers,
      groupsWithTextVoteCastedPlayers,
      myGroup,
      // Host / moderator / player rules, shared with the game screen and the replay.
      ...useGameViewer(),
      elementViewPhases: ELEMENT_VIEW_PHASES,
    };
  },
  watch: {
    "game.phase": function () {
      this.timerExpiredPhase = null;
    },
    allVotesCasted(newVal) {
      if (!newVal) return;

      // Only show this modal in phases where it makes sense
      if (!["PRELIMINARY_VOTE", "DISCUSS", "VOTE"].includes(this.game?.phase))
        return;

      // In DISCUSS: do NOT show "All votes casted" modal if consensus is already reached
      if (this.game?.phase === "DISCUSS" && this.consensusReached) return;

      if (!this.showFullVotesReceivedModal) {
        this.openFullVotesReceivedModal();
      }
    },
    consensusReached(newVal, oldVal) {
      if (newVal && !oldVal && !this.showConsensusModal) {
        this.openConsensusModal();
      }
      // optional: if you DO want to auto-close when it stops being true:
      else if (!newVal && this.showConsensusModal) this.closeConsensusModal();
    },
    // groupsWithTextVotes: function (newVal) {
    //   console.log("Watching groupsWithTextVotes: ", newVal);

    //   if (!this.isHost) return; // host-only
    //   if (!(this.game && this.game.phase === "DISCUSS")) return;

    //   // Check if all groups have submitted their text votes
    //   let cGroups = 0;
    //   newVal.forEach((group) => {
    //     const voteMap = {};
    //     group.textVotes.forEach((text) => {
    //       if (text.id) {
    //         voteMap[text.id] = text.votes || 0;
    //       }
    //     });

    //     const groupPlayersCount = group.players ? group.players.length : 0;
    //     const nMinusOne = groupPlayersCount - 1;

    //     const anyTextWithEnoughVotes = Object.values(voteMap).some((voteCount) => voteCount >= nMinusOne);

    //     cGroups = anyTextWithEnoughVotes ? cGroups + 1 : cGroups;
    //   });

    //   if (cGroups === newVal.length && newVal.length > 0) {
    //     this.showConsensusModal = true;
    //   }
    // },
    "gameGroupStore.playersIndex": {
      handler(val) {
        if (!this.isHost) return;
        if (!val || Object.keys(val).length === 0) return;
        if (this.hostHydrated) return;

        this.onPlayersReadyForHost();
      },
      deep: true,
    },
  },
  mounted() {
    this.isLoading = false;
    if (!this.game) {
      this.$router.push({ name: "home" });
      return;
    }
    this.listenToPlayers(this.game.fbGameDocId);
    this.gameStore.forceHideSaboteurText();
    if (this.isModerator) {
      this.fetchPlayerRole();
    }

    this.listenToGameChanges(this.game.fbGameDocId);

    const isReconnectFlow = sessionStorage.getItem("isReconnectFlow") === "true";
    sessionStorage.removeItem("isReconnectFlow");

    if (isReconnectFlow) {
      this.isReconnectFlow = true;
      this.handleRejoinState();
    }
  },
  beforeUnmount() {
    // Unsubscribe from the players snapshot if it exists
    if (this.fbPlayersSnapshot) {
      // console.log("Unsubscribing from players listener...");
      this.fbPlayersSnapshot();
      this.fbPlayersSnapshot = null; // Clean up reference
    }

    // Unsubscribe from the game snapshot if it exists
    if (this.fbGameSnapshot) {
      // console.log("Unsubscribing from game listener...");
      this.fbGameSnapshot();
      this.fbGameSnapshot = null; // Clean up reference
    }

    // Unsubscribe from the messages snapshot if it exists
    if (this.fbMessagesSnapshot) {
      // console.log("Unsubscribing from messages listener...");
      this.fbMessagesSnapshot();
      this.fbMessagesSnapshot = null; // Clean up reference
    }

    // Unsubscribe from the all groups messages snapshot if it exists
    if (this.fbAllChatsUnsubs) {
      Object.values(this.fbAllChatsUnsubs).forEach((unsub) => unsub && unsub());
      this.fbAllChatsUnsubs = {};
    }
  },
  computed: {
    $style() {
      return styles;
    },
    totalVotesCasted() {
      if (this.game.phase === "PRELIMINARY_VOTE") {
        return (
          this.groupsWithPrelimVoteCastedPlayers?.reduce(
            (s, g) => s + (g?.votesCastedCount ?? 0),
            0,
          ) ?? 0
        );
      }
      if (this.game.phase === "DISCUSS") {
        return (
          this.groupsWithTextVoteCastedPlayers?.reduce(
            (s, g) => s + (g?.votesCastedCount ?? 0),
            0,
          ) ?? 0
        );
      }
      if (this.game.phase === "VOTE") {
        return (
          this.groupsWithSaboteurVoteCastedPlayers?.reduce(
            (s, g) => s + (g?.votesCastedCount ?? 0),
            0,
          ) ?? 0
        );
      }
      return 0;
    },
    totalPlayersCount() {
        const players = this.game?.players ?? [];
        return players.filter(
          (p) => p?.role === "player" || p?.role === "saboteur"
        ).length;
    },
    allVotesCasted() {
      return (
        this.totalPlayersCount > 0 &&
        this.totalPlayersCount === this.totalVotesCasted
      );
    },
    isChatReadOnly() {
      return !this.isHost && this.game?.phase === "VOTE";
    },
    consensusReached() {
      // Only care during DISCUSS and for host
      if (!(this.isHost && this.game?.phase === "DISCUSS")) return false;

      const groups = this.groupsWithTextVoteCastedPlayers ?? [];
      // Guard against empty during initial hydration
      if (groups.length === 0) return false;

      return groups.every((g) => g?.consensusReached === true);
    },
    canControlPhase() {
      return this.isHost;
    },








    viewReady() {
      if (!this.isHost && !this.currentUserPlayer?.role) {
        return false;
      }
      // moderators should never render JOIN
      if (this.isModerator && this.game?.phase === "JOIN") {
        return false;
      }
      if (this.game?.phase !== "READ") {
        return true;
      }
      const groups = this.displayGroupsWithPlayers ?? [];
      if (this.isHost) {
        return groups.some(g => (g.players ?? []).length > 0);
      }
      if (this.isModerator) {
        return (
          this.moderatorGroupIds.length > 0 &&
          groups.some(g => (g.players ?? []).length > 0)
        );
      }
      return true;
    },
  },
  methods: {
    startGame() {
      // console.log("initialgroups: ", this.initialGroups);
      if (this.isHost) {
        this.showStartGameConfirmationModal = true;
      }
    },
    async handleStartGame() {
      try {
        this.isLoading = true;
        this.showStartGameConfirmationModal = false;
        const cleanedGroups = this.initialGroups.filter(
          (group) => (group.players || []).length > 0
        );

        const payloadGroups = cleanedGroups.map((group, index) => ({
          id: index + 1,
          name: group.name || this.$t("group-label", { id: index + 1 }),
          playerIds: group.players.map((player) => Number(player.id)),
        }));

        const moderators = {};

        Object.entries(this.pendingModerators || {}).forEach(
          ([playerId, groupIndexes]) => {

            moderators[Number(playerId)] =
              groupIndexes.map(idx => idx + 1);
          }
        );

        // console.log(
        //   "START GAME initialGroups:",
        //   JSON.parse(JSON.stringify(this.initialGroups))
        // );
        // console.log("START GAME pendingModerators:", this.pendingModerators);
        // console.log("START GAME moderators:", moderators);

        const startGameRes = await gameService.startGame({
          gameId: this.game.gameId,
          groups: payloadGroups,
          moderators,
        });

        if (startGameRes && startGameRes.status === 200 && startGameRes.data) {
          if (startGameRes.data.groups) {
            this.gameGroupStore.setGroups(startGameRes.data.groups);
          }
        } else {
          console.error("Some error occurred while starting the game!");
          toast(
            "Some error occurred while starting the game. Please try again!",
            {
              type: "error",
            },
          );
          return;
        }

        await gameService.nextPhase({ joinCode: String(this.game.joinCode)});
      } catch (error) {
        console.error("Error while starting the game: ", error);
        const apiMsg =
          error?.response?.data?.message || error?.response?.data?.detail;

          if (apiMsg) {
              toast(apiMsg, { type: "error" });
                  } else {
                    toast(this.$t("unexpected-error"), {
              type: "error",
            });
          }
      } finally {
        this.isLoading = false;
      }
    },
    clearRejoinState() {
      const lastJoinCode = localStorage.getItem("lastJoinCode");

      if (lastJoinCode) {
        localStorage.removeItem(`rejoinToken_${lastJoinCode}`);
      }

      localStorage.removeItem("lastJoinCode");
      localStorage.removeItem("lastPlayerName");
    },
    async handleRejoinState() {
      try {
        this.isRehydrating = true;
        const res = await gameService.rehydrateGame({
          playerId: this.user.id,
        });
        const data = res?.data;
        if (!data) {
          throw new Error("Missing hydration data");
        }
        // -----------------------------------
        // RESTORE GAME
        // -----------------------------------
        this.gameStore.restoreHydratedGame(data);

        // -----------------------------------
        // HOST
        // -----------------------------------

        if (data.role === "host") {
            this.gameGroupStore.hydrateGroups(data.groups);
            this.gameStore.updatePlayers(data.players ?? []);
            this.listenToPlayers(this.game.fbGameDocId);
            this.listenToAllMessages(this.game.fbGameDocId);
            const phase = this.game?.phase;
            if (phase === "PRELIMINARY_VOTE" || phase === "DISCUSS") {
              await this.fetchPrelimVotes();
            }
            if (phase === "DISCUSS") {
              this.tabSelected = "gameTextsVote";
            }
            if (phase === "VOTE") {
              await this.fetchAllGroupsVotes();
            }
            if (phase === "REVEAL" || phase === "DEBRIEF") {
              this.tabSelected = "leaderboard";
              await this.fetchGameResults();
              if (phase === "DEBRIEF") {
                this.listenToAllMessages(this.game.fbGameDocId);
                this.tabSelected = "gameTexts";
              }
            }
          }

        // -----------------------------------
        // MODERATOR
        // -----------------------------------

        else if (data.role === "moderator") {
          const groups = data.groups ?? [];
          const moderatorGroupIds =
            data.moderatorGroupIds ?? groups.map((g) => Number(g.id));

          this.gameGroupStore.hydrateGroups(groups);
          this.gameGroupStore.setModeratorGroups(moderatorGroupIds);

          if (moderatorGroupIds.length > 0) {
            this.gameGroupStore.setCurrentGroupId(moderatorGroupIds[0]);
          }

          this.gameStore.updatePlayers(data.players ?? []);

          groups.forEach((group) => {
            if (group.saboteur) {
              this.gameGroupStore.setGroupSaboteur(
                group.id,
                group.saboteur
              );
            }
          });

          this.listenToPlayers(this.game.fbGameDocId);
          this.listenToAllMessages(this.game.fbGameDocId);

          const phase = this.game?.phase;

          if (phase === "PRELIMINARY_VOTE" || phase === "DISCUSS") {
            await this.fetchPrelimVotes();

            if (phase === "DISCUSS") {
              this.tabSelected = "gameTextsVote";
            }
          }

          if (phase === "VOTE") {
            await this.fetchAllGroupsVotes();
          }

          if (phase === "REVEAL" || phase === "DEBRIEF") {
            await this.fetchGameResults();

            this.tabSelected =
              phase === "DEBRIEF" ? "gameTexts" : "leaderboard";
          }
        }

        // -----------------------------------
        // PLAYER / SABOTEUR
        // -----------------------------------
        else {
          const group = data.group;
          this.gameGroupStore.setMyGroup(
            group.id,
            {
              id: group.id,
              name: group.name,
            },
            group.fbDocId
          );
          await this.fetchPlayerRole();
          this.listenToPlayers(this.game.fbGameDocId);
          await this.$nextTick();
          const hydratedGroup =
            this.gameGroupStore.myGroup;

          if (hydratedGroup?.fbDocId) {
            this.listenToGroupMessages(
              this.game.fbGameDocId,
              hydratedGroup.fbDocId,
              hydratedGroup.id,
            );
          }
          const phase = this.game?.phase;

          // ✅ PRELIM VOTES
          if (phase === "PRELIMINARY_VOTE" || phase === "DISCUSS") {
            await this.fetchPrelimVotes();
            if (phase === "DISCUSS") {
              this.tabSelected = "gameTextsVote";
            }
          }
          // ✅ FINAL VOTES
          if (phase === "VOTE") {
            await this.fetchGroupVotes();
          }
          // ✅ RESULTS
          if (phase === "REVEAL" || phase === "DEBRIEF") {
            this.tabSelected = "leaderboard";
            await this.fetchGameResults();
            // DEBRIEF needs chats restored too
            if (phase === "DEBRIEF") {

              if (this.myGroup?.fbDocId) {
                this.listenToGroupMessages(
                  this.game.fbGameDocId,
                  this.myGroup.fbDocId,
                  this.myGroup.id,
                );
              }
              // force default debrief tab
              this.tabSelected = "gameTexts";
            }
          }
        }
      } catch (err) {
        console.error("Rejoin hydration failed:", err);
      } finally {
        setTimeout(() => {
          this.isRehydrating = false;
          // reconnect UI done
          this.isReconnectFlow = false;
        }, 300);
      }
    },
    // async rehydrateHost() {
    //   this.hostHydrated = false;
    //   this.isLoading = true;

    //   // 1. Load groups
    //   const res = await gameService.getGroups({ gameId: this.game.gameId });
    //   if (res?.data?.groups) {
    //     this.gameGroupStore.setGroups(res.data.groups);
    //   }

    //   // 2. Load players explicitly (IMPORTANT)
    //   const playersRes = await gameService.getPlayers({ gameId: this.game.gameId });
    //   if (playersRes?.data?.players) {
    //     this.gameStore.updatePlayers(playersRes.data.players);
    //   }

    //   // 3. Load phase-dependent data
    //   const phase = this.game.phase;

    //   if (phase === "PRELIMINARY_VOTE" || phase === "DISCUSS") {
    //     await this.fetchPrelimVotes();
    //   }

    //   if (phase === "DISCUSS") {
    //     await this.fetchAllGroupsVotes(); // text votes
    //   }

    //   if (phase === "VOTE") {
    //     await this.fetchAllGroupsVotes();
    //   }

    //   if (phase === "REVEAL" || phase === "DEBRIEF") {
    //     await this.fetchGameResults();
    //   }

    //   this.hostHydrated = true;
    //   // ✅ FORCE restore real text (for ALL roles)
    //   const roleRes = await gameService.getPlayerRole2({
    //     playerId: this.user.id,
    //   });

    //   if (roleRes?.data?.realTextId != null) {
    //     this.gameStore.updateRealText(roleRes.data.realTextId);
    //     this.gameStore.hostShowSaboteurText = true;
    //   }
    //   // 4. THEN attach listeners
    //   this.listenToPlayers(this.game.fbGameDocId);
    //   this.listenToAllMessages(this.game.fbGameDocId);
    //   this.isLoading = false;
    // },
    // async rehydratePlayer() {
    //   this.isLoading = true;

    //   try {
    //     // restore player group + role context
    //     await this.startPlayerGame();

    //     const phase = this.game.phase;

    //     if (phase === "PRELIMINARY_VOTE" || phase === "DISCUSS") {
    //       await this.fetchPrelimVotes();
    //     }

    //     if (phase === "VOTE") {
    //       await this.fetchGroupVotes();
    //     }

    //     if (phase === "REVEAL" || phase === "DEBRIEF") {
    //       await this.fetchGameResults();
    //     }

    //     this.listenToPlayers(this.game.fbGameDocId);

    //     if (this.isModerator) {
    //       this.listenToAllMessages(this.game.fbGameDocId);
    //     }

    //     const roleRes = await gameService.getPlayerRole2({
    //       playerId: this.user.id,
    //     });

    //     if (roleRes?.data?.realTextId != null) {
    //       this.gameStore.updateRealText(roleRes.data.realTextId);

    //       if (this.isModerator) {
    //         this.gameStore.moderatorShowSaboteurText = true;
    //       }
    //     }
    //   } finally {
    //     this.isLoading = false;
    //   }
    // },
    async startPlayerGame() {
      try {
        if (this.isHost) return;
        this.isLoading = true;

        const playerDetailsRes = await gameService.getPlayerGroupDetails({
          playerId: this.user.id,
        });

        if (
          playerDetailsRes &&
          playerDetailsRes.status === 200 &&
          playerDetailsRes.data
        ) {
          if (
            Array.isArray(playerDetailsRes.data?.groups) &&
            playerDetailsRes.data.groups.length > 0
          ) {
            const playerGroup = playerDetailsRes.data.groups[0];
            if (this.isModerator) {

              for (const groupData of playerDetailsRes.data.groups) {

                const resolvedGroupId = Number(groupData.id);

                const existingGroup =
                  this.gameGroupStore.groupById?.[resolvedGroupId];

                this.gameGroupStore.groupById = {
                  ...this.gameGroupStore.groupById,
                  [resolvedGroupId]: {
                    ...(existingGroup || {}),
                    ...groupData,
                    id: resolvedGroupId,
                    fbDocId: groupData.fbGroupDocId ?? null,
                  },
                };

                if (!this.gameGroupStore.groupIds.includes(resolvedGroupId)) {
                  this.gameGroupStore.groupIds = [
                    ...this.gameGroupStore.groupIds,
                    resolvedGroupId,
                  ];
                }

                if (groupData?.saboteur) {
                  this.gameGroupStore.setGroupSaboteur(
                    resolvedGroupId,
                    groupData.saboteur
                  );
                }
              }

              this.gameGroupStore.setModeratorGroups(
                playerDetailsRes.data.groups.map(g => Number(g.id))
              );

            if (playerDetailsRes.data?.realTextId != null) {
              this.gameStore.updateRealText(playerDetailsRes.data.realTextId);
            }
            } else {
              this.gameGroupStore.setMyGroup(
                Number(playerGroup.id),
                {
                  ...playerGroup,
                },
                playerGroup.fbGroupDocId,
              );
            }
          }
        } else {
          console.error("Some error occurred while fetching player details!");
          toast(
            this.$t("unexpected-error"),
            {
              type: "error",
            }
          );
          return;
        }
      } catch (error) {
        console.error("Error while starting the game (player): ", error);
        toast(
          this.$t("unexpected-error"),
          {
            type: "error",
          }
        );
      } finally {
        //this.isLoading = false;
      }
    },
    async continueGame() {
      if (!this.canControlPhase) return;
      if (this.game.phase === "PRELIMINARY_VOTE") {
        // check if all the prelim votes have been casted
        const votesCasted =
          this.groupsWithPrelimVoteCastedPlayers?.reduce(
            (sum, g) => sum + (g?.votesCastedCount ?? 0),
            0,
          ) ?? 0;
        if (votesCasted == this.totalPlayersCount) {
          this.advanceToNextPhase();
        } else {
          // show modal
          this.showMissingVotesModal = true;
        }
      } else if (this.game.phase === "DISCUSS") {
        const groups = this.groupsWithTextVoteCastedPlayers ?? [];

        const votesCasted = groups.reduce(
          (sum, g) => sum + (g?.votesCastedCount ?? 0),
          0,
        );

        const totalPlayers = this.totalPlayersCount;

        // 1️⃣ Some players have NOT voted → MissingVotesModal
        if (votesCasted !== totalPlayers) {
          this.showMissingVotesModal = true;
          return;
        }

        // 2️⃣ All players voted → check consensus
        const allGroupConsensusReached = groups.every(
          (g) => g.consensusReached,
        );

        // 3️⃣ Consensus reached → auto advance
        if (allGroupConsensusReached) {
          this.advanceToNextPhase();
        } else {
          // 4️⃣ All voted but no consensus → confirmation modal (like prelim)
          this.openFullVotesReceivedModal();
        }
      } else if (this.game.phase === "VOTE") {
        // check if all the saboteur votes have been casted
        const votesCasted =
          this.groupsWithSaboteurVoteCastedPlayers?.reduce(
            (sum, g) => sum + (g?.votesCastedCount ?? 0),
            0,
          ) ?? 0;
        if (votesCasted == this.totalPlayersCount) {
          // if (this.game.votedPlayersCount == this.game.players.length) {
          this.advanceToNextPhase();
        } else {
          // show modal
          this.showMissingVotesModal = true;
        }
      } else {
        this.advanceToNextPhase();
      }
    },
    async onPlayersReadyForHost() {
      // prevent duplicate execution
      if (Object.keys(this.fbAllChatsUnsubs).length > 0) return;
      if (this.hostHydrated) return;
      if (!this.isHost) return;
      if (!this.game?.fbGameDocId) return;
      this.hostHydrated = true;

      // ✅ NOW SAFE: groups + players both ready

      // attach chats
      this.listenToAllMessages(this.game.fbGameDocId);

      const phase = this.game?.phase;

      if (phase === "PRELIMINARY_VOTE") {
        await this.fetchPrelimVotes();
      }

      if (phase === "DISCUSS") {
        this.tabSelected = "gameTextsVote";
        await this.fetchPrelimVotes();
      }

      if (phase === "VOTE") {
        await this.fetchAllGroupsVotes();
      }

      if (phase === "REVEAL" || phase === "DEBRIEF") {
        await this.fetchGameResults();
      }
    },
    async endGame() {
      if (!this.canControlPhase) return;
      this.showEndGameConfirmationModal = true;
    },
    async advanceToNextPhase() {
      if (!this.canControlPhase) return;
      this.isLoading = true;

      // @see: this is only triggered for the host
      // wait until scores are calcuated
      // players will see the loading screen until this is done
      if (this.game.phase === "PRELIMINARY_VOTE") {
        await gameService.preparePrelimVotes({ gameId: this.game.gameId });
        await gameService.preparePlayersRole({ gameId: this.game.gameId });
      } else if (this.game.phase === "VOTE") {
        await gameService.calculateScores({ gameId: this.game.gameId });
        await gameService.prepareSaboteurVotes({ gameId: this.game.gameId });
      }
      await gameService.nextPhase({ joinCode: String(this.game.joinCode) });
    },
    copyJoinCode() {
      // @TODO: need to implement copy code
      alert("feature coming soon ...");
    },
    shareJoinCode() {
      // @TODO: need to implement share code
      alert("feature coming soon ...");
    },
    onTimerEnd() {
      if (this.timerExpiredPhase === this.game?.phase) return;
      this.timerExpiredPhase = this.game?.phase || null;
    },
    async sendMessage(text, groupId) {
      try {
        if (text instanceof PointerEvent) {
          console.warn("PointerEvent detected, ignoring...");
          return;
        }

        // console.log("send message text: ", text);

        // @see: Updated on Oct 2, 2025
        // sending all the required details for the API in payload
        // to test if this would reduce the chat lag

        const msgObj = {
          senderName: this.user.name,
          senderId: this.user.id,
          senderAvatar: this.user.avatar,
          isHost: this.isHost,
          isModerator: this.isModerator,
          text: text,
          phase: this.game.phase,
          groupId: groupId,
          gameId: this.game.gameId,
        };
        // console.log("msgObj: ", msgObj);

        let sendMessageResponse = await gameService.sendMessage2(msgObj);
        if (sendMessageResponse && sendMessageResponse.status === 200) {
          // do anything if needed
          // continue
          // console.log("pushign message----");
          // this.gameStore.addMessage({
          //   doc_id: "zxgghh",
          //   user_name: "Player 2",
          //   user_id: 3,
          //   is_host: false,
          //   created_at: "firestore.SERVER_TIMESTAMP",
          //   text: "Message from player 2. spme new ,messaeg to test",
          //   user_avatar: "https://api.dicebear.com/7.x/bottts-neutral/svg?radius=20&scale=80&seed=Player 2",
          // });
        } else {
          toast(
            this.$t("unexpected-error"),
            {
              type: "error",
            },
          );
        }
      } catch (error) {
        console.error("Error while trying to send a message: ", error);
        toast(
          this.$t("unexpected-error"),
          {
            type: "error",
          },
        );
      }
    },
    async updateTextVote(textId) {
      try {
        this.isLoading = true;

        // console.log("playgameview - updateTextVote - textId: ", textId);

        var response = await gameService.updateTextVote({
          gameId: this.game.gameId,
          textId: textId,
          voterId: this.user.id,
        });

        // console.log("response: ", response);

        if (response && response.status === 200) {
          // Successfully updated text vote
          // console.log("Text vote updated successfully.");
          toast(this.$t("text-vote-success"), {
            type: "success",
          });
        } else {
          // Handle error case
          console.error("Failed to update text vote.");
          toast(
            this.$t("text-vote-error"),
            {
              type: "error",
            },
          );
        }
      } catch (error) {
        console.error("Error while trying to update text vote: ", error);
        toast(
          this.$t("text-vote-error"),
          {
            type: "error",
          },
        );
      } finally {
        this.isLoading = false;
      }
    },
    async submitPrelimVote(prelimVoteObj) {
      try {
        this.isLoading = true;

        // console.log("prelimVoteObj: ", prelimVoteObj);
        prelimVoteObj.voterId = this.user.id;
        // console.log("prelimVoteObj after: ", prelimVoteObj);

        await gameService.submitPreliminaryVote(prelimVoteObj);
        this.justSubmittedPrelim = true;

        // let allPrelimVotedRes = await gameService.allPlayersPrelimVoted2({ joinCode: this.game.joinCode });
        // if (
        //   allPrelimVotedRes &&
        //   allPrelimVotedRes.status === 200 &&
        //   allPrelimVotedRes.data &&
        //   allPrelimVotedRes.data.allVoted
        // ) {
        //   console.log("all voted ---");

        //   this.advanceToNextPhase();
        // }
        return true;
      } catch (error) {
        console.error("Error while trying to submit a prelim vote: ", error);
        toast(
          this.$t("unexpected-error"),
          {
            type: "error",
          },
        );

        return false;
      } finally {
        this.isLoading = false;
      }
    },
    async submitVote(voteObj) {
      try {
        this.isLoading = true;

        // console.log("voteObj: ", voteObj);
        voteObj.voterId = this.user.id;
        // console.log("voteObj after: ", voteObj);

        // await gameService.submitVote(voteObj);
        await gameService.submitVote_v2(voteObj);
        this.justSubmittedSaboteur = true;

        // let allVotedRes = await gameService.allPlayersVoted2({ joinCode: this.game.joinCode });
        // if (allVotedRes && allVotedRes.status === 200 && allVotedRes.data && allVotedRes.data.allVoted) {
        //   console.log("all voted ---");
        //   this.advanceToNextPhase();
        // }

        return true;
      } catch (error) {
        console.error("Error while trying to submit a prelim vote: ", error);
        toast(
          this.$t("unexpected-error"),
          {
            type: "error",
          },
        );

        return false;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchPrelimVotes() {
      try {
        // Call the API to get preliminary votes using gameService
        const prelimVotesListRes = await gameService.getPrelimVotes2({
          gameId: this.game.gameId,
        });

        // console.log("prelimVotesListRes: ", prelimVotesListRes);

        if (
          prelimVotesListRes &&
          prelimVotesListRes.status === 200 &&
          prelimVotesListRes.data &&
          prelimVotesListRes.data.preliminaryVotes
        ) {
          // console.log("about to sned to store");
          this.gameStore.addPreliminaryVotesList(
            prelimVotesListRes.data.preliminaryVotes,
          );
        } else {
          toast(
            this.$t("unexpected-error"),
            {
              type: "error",
            },
          );
        }
      } catch (error) {
        console.error("Error fetching preliminary votes:", error);
        toast(
          this.$t("unexpected-error"),
          {
            type: "error",
          },
        );
      }
    },
    closeEndGameConfirmationModal() {
      this.showEndGameConfirmationModal = false;
    },
    confirmEndGame() {
      this.showEndGameConfirmationModal = false;
      this.advanceToNextPhase();
    },
    async fetchAllGroupsScores() {
      try {
        // Call the API to get scores using gameService
        const gameScoresRes = await gameService.getAllGroupsScores({
          gameId: this.game.gameId,
        });

        // console.log("gameScoresRes: ", gameScoresRes);

        if (
          gameScoresRes &&
          gameScoresRes.status === 200 &&
          gameScoresRes.data &&
          gameScoresRes.data.players
        ) {
          if (gameScoresRes.data.winners) {
            this.gameGroupStore.setWinners(gameScoresRes.data.winners);
          }
          // console.log("about to send to store");
          // this.gameStore.updatePlayerScores(gameScoresRes.data.players);
          this.gameStore.updateAllGroupsPlayerScores(
            gameScoresRes.data.players,
          );

          // @see: already updated in previous phases
          // this.gameStore.updateSaboteur(gameScoresRes.data.saboteurId ?? null);
          // this.gameStore.updateRealText(gameScoresRes.data.realTextId ?? null);
        } else {
          toast(
            this.$t("unexpected-error"),
            {
              type: "error",
            },
          );
        }
      } catch (error) {
        console.error("Error fetching game scores:", error);
        toast(
          this.$t("unexpected-error"),
          {
            type: "error",
          },
        );
      }
    },
    async fetchGroupScores() {
      try {
        // Call the API to get scores using gameService
        const groupScoresRes = await gameService.getGroupScores({
          gameId: this.game.gameId,
          groupId: this.myGroup.id,
        });

        // console.log("groupScoresRes: ", groupScoresRes);

        if (
          groupScoresRes &&
          groupScoresRes.status === 200 &&
          groupScoresRes.data &&
          groupScoresRes.data.players
        ) {
          if (groupScoresRes.data.winner) {
            this.gameWinner = groupScoresRes.data.winner;
            if (this.game?.phase === "REVEAL" && !this.isReconnectFlow) {
              this.showWinnerModal = true;
            }
          }
          // console.log("about to send to store");
          this.gameStore.updateGroupPlayersScores(groupScoresRes.data.players);
          // this.gameStore.updatePlayerScores(groupScoresRes.data.players);
          this.gameStore.updateSaboteur(groupScoresRes.data.saboteurId ?? null);
          this.gameStore.updateRealText(groupScoresRes.data.realTextId ?? null);
        } else {
          toast(
            this.$t("unexpected-error"),
            {
              type: "error",
            },
          );
        }
      } catch (error) {
        console.error("Error fetching group scores:", error);
        toast(
          this.$t("unexpected-error"),
          {
            type: "error",
          },
        );
      }
    },
    async fetchAllGroupsVotes() {
      try {
        const votesRes = await gameService.getAllGroupsVotes({
          gameId: this.game.gameId,
        });

        // console.log("all groups votesRes: ", votesRes);

        if (votesRes && votesRes.status === 200 && votesRes.data) {
          // console.log("about to send to store");

          this.gameGroupStore.setAllGroupsSaboteurVotes(
            votesRes.data.saboteurVotes ? votesRes.data.saboteurVotes : null,
          );
        } else {
          toast(
            this.$t("unexpected-error"),
            {
              type: "error",
            },
          );
        }
      } catch (error) {
        console.error("Error fetching final votes:", error);
        toast(
          this.$t("unexpected-error"),
          {
            type: "error",
          },
        );
      }
    },
    async fetchGroupVotes() {
      try {
        const votesRes = await gameService.getGroupVotes({
          gameId: this.game.gameId,
          groupId: this.myGroup.id,
        });

        // console.log("group votesRes: ", votesRes);

        if (votesRes && votesRes.status === 200 && votesRes.data) {
          // console.log("about to send to store");

          // already in descending ordeer
          this.gameGroupStore.setGroupSaboteurVotes(
            votesRes.data.saboteurVotes ? votesRes.data.saboteurVotes : null,
            this.myGroup.id,
          );
        } else {
          toast(
            this.$t("unexpected-error"),
            {
              type: "error",
            },
          );
        }
      } catch (error) {
        console.error("Error fetching final votes:", error);
        toast(
          this.$t("unexpected-error"),
          {
            type: "error",
          },
        );
      }
    },
    async fetchVotes() {
      try {
        // Call the API to get scores using gameService
        const votesRes = await gameService.revealVotes2({
          gameId: this.game.gameId,
        });

        // console.log("votesRes: ", votesRes);

        if (votesRes && votesRes.status === 200 && votesRes.data) {
          // console.log("about to send to store");
          // @see: text votes are already being listened to in real-time from firestore
          // this.gameStore.addVoteResults(
          //   votesRes.data.sortedTextVotes ? votesRes.data.sortedTextVotes : null,
          //   votesRes.data.sortedSaboteurVotes ? votesRes.data.sortedSaboteurVotes : null
          // );
          this.gameStore.addSaboteurVotes(
            votesRes.data.sortedSaboteurVotes
              ? votesRes.data.sortedSaboteurVotes
              : null,
          );
        } else {
          toast(
            this.$t("unexpected-error"),
            {
              type: "error",
            },
          );
        }
      } catch (error) {
        console.error("Error fetching final votes:", error);
        toast(
          this.$t("unexpected-error"),
          {
            type: "error",
          },
        );
      }
    },
    async fetchGameResults() {
      try {
        this.isLoading = true;
        this.scoresLoadedForReveal = false;

        if (this.isHost || this.isModerator) {
          await this.fetchAllGroupsScores();
          await this.fetchAllGroupsVotes();
        } else {
          await this.fetchGroupScores();
          await this.fetchGroupVotes();
        }
        this.scoresLoadedForReveal = true;
      } catch (error) {
        console.error(
          "Some error occurred while fetching game results!",
          error
        );
      } finally {
        this.isLoading = false;
      }
    },
    async fetchGroupScoresById(groupId) {
      try {
        const groupScoresRes = await gameService.getGroupScores({
          gameId: this.game.gameId,
          groupId,
        });

        if (
          groupScoresRes &&
          groupScoresRes.status === 200 &&
          groupScoresRes.data &&
          groupScoresRes.data.players
        ) {
          if (groupScoresRes.data.winner) {
            this.gameWinner = groupScoresRes.data.winner;
            if (this.game?.phase === "REVEAL" && !this.isReconnectFlow) {
              this.showWinnerModal = true;
            }
          }

          this.gameStore.updateGroupPlayersScores(groupScoresRes.data.players);
          this.gameStore.updateSaboteur(groupScoresRes.data.saboteurId ?? null);
          this.gameStore.updateRealText(groupScoresRes.data.realTextId ?? null);
        } else {
          toast(
            "Some error occurred while fetching the group scores. Please try again!",
            { type: "error" }
          );
        }
      } catch (error) {
        console.error("Error fetching group scores:", error);
        toast(
          "Some error occurred while fetching the group scores. Please try again!",
          { type: "error" }
        );
      }
    },
    async fetchGroupVotesById(groupId) {
      try {
        const votesRes = await gameService.getGroupVotes({
          gameId: this.game.gameId,
          groupId,
        });

        if (votesRes && votesRes.status === 200 && votesRes.data) {
          this.gameGroupStore.setGroupSaboteurVotes(
            votesRes.data.saboteurVotes ? votesRes.data.saboteurVotes : null,
            groupId,
          );
        } else {
          toast(
            "Some error occurred while fetching group final votes. Please try again!",
            { type: "error" }
          );
        }
      } catch (error) {
        console.error("Error fetching final votes:", error);
        toast(
          "Some error occurred while fetching group final votes. Please try again!",
          { type: "error" }
        );
      }
    },
    endGamePlay() {
      // unsubscribe
      if (this.fbMessagesSnapshot) {
        this.fbMessagesSnapshot();
        this.fbMessagesSnapshot = null;
      }
      if (this.fbGameSnapshot) {
        this.fbGameSnapshot();
        this.fbGameSnapshot = null;
      }
      if (this.fbPlayersSnapshot) {
        this.fbPlayersSnapshot();
        this.fbPlayersSnapshot = null;
      }
      if (this.fbAllChatsUnsubs) {
        Object.values(this.fbAllChatsUnsubs).forEach(
          (unsub) => unsub && unsub(),
        );
        this.fbAllChatsUnsubs = {};
      }
      // Remove reconnect token after game completion
      const joinCode = this.game?.joinCode;
      if (joinCode) {
        localStorage.removeItem(`rejoinToken_${joinCode}`);
      }
      // navogate to Thank You page
      this.$router.push({ name: "thank-you" });
    },
    listenToGameChanges(gameDocId) {
      // console.log("fb game doc id in listen to phases: ", gameDocId);
      if (!gameDocId || typeof gameDocId !== "string") {
        // console.warn("listenToGameChanges: invalid gameDocId", gameDocId);
        return;
      }

      const gameRef = doc(fireDB, "games", gameDocId);

      this.fbGameSnapshot = onSnapshot(gameRef, async (snapshot) => {
        if (!snapshot.exists()) {
          // console.warn("Game doc does not exist yet:", gameDocId);
          return;
        }

        const data = snapshot.data();
        this.game.phaseLoading = data.phaseLoading === true;
        ["phaseStartedAt", "phaseEndsAt", "phaseDurationSeconds"].forEach(
          (field) => {
            if (Object.prototype.hasOwnProperty.call(data, field)) {
              this.game[field] = data[field];
            }
          }
        );

        if (!data || typeof data.phase === "undefined") {
          // console.warn("Game doc has no phase:", data);
          return;
        }
        // console.log("game changes ", snapshot.data());
        // console.log("current phase", this.game.phase);
        // let liveGamePhase = snapshot.data().phase;
        const liveGamePhase = data.phase;
        const currentPhase = this.game?.phase; // safe read

        if (liveGamePhase !== currentPhase) {
          // let timerPhases = ["READ", "PRELIMINARY_VOTE", "DISCUSS", "VOTE", "REVEAL", "DEBRIEF"];
          switch (liveGamePhase) {
            case "READ":
              this.isLoading = true;
              if (!this.isHost) {
                await this.startPlayerGame();
              }

              // console.log("--- about to set the READ timer: ", this.game.readingTime * 60);
              // this.phaseTimerDuration = this.game.readingTime * 60;
              break;
            case "PRELIMINARY_VOTE":
              await this.fetchPrelimVotes(this.game.gameId);
              // this.phaseTimerDuration = 2 * 60;
              break;
            case "DISCUSS":
              this.fetchPrelimVotes(this.game.gameId);

              // start listening to messages
              if (this.isHost || this.isModerator) {
                this.listenToAllMessages(this.game.fbGameDocId);
              } else {
                this.listenToGroupMessages(
                  this.game.fbGameDocId,
                  this.myGroup.fbDocId,
                  this.myGroup.id,
                );
              }

              // this.tabSelected = "gameTexts";
              this.tabSelected = "gameTextsVote";

              if (!this.isHost || this.isModerator) {
                this.fetchPlayerRole();

                // if (this.isSaboteur) {
                //   // show a toast to saboteur
                //   toast("Actively participate to discuss and mislead others into choosing wrong texts!", {
                //     type: "info",
                //   });
                // } else {
                //   // show a toast to player
                //   toast("Actively participate to discuss and vote for the best text to score points!", {
                //     type: "info",
                //   });
                // }
              }

              // this.phaseTimerDuration = this.game.discussionTime * 60;
              break;
            case "VOTE":
              // this.phaseTimerDuration = 2 * 60;
              // ensure any DISCUSS modal state doesn't bleed into VOTE
              this.showFullVotesReceivedModal = false;
              this.showConsensusModal = false;
              this.showMissingVotesModal = false;
              break;
            case "REVEAL":
              this.isLoading = true;
              this.tabSelected = "leaderboard";
              await this.fetchGameResults();

              // this.phaseTimerDuration = 5 * 60;
              break;
            case "DEBRIEF":
              this.tabSelected = "gameTexts";

              // this.phaseTimerDuration = 5 * 60;
              break;
            case "DOWNLOAD":
            case "END":
              //console.log("END PHASE HIT");
              this.endGamePlay();
              break;
          }

          // update store @end
          this.gameStore.updateGamePhase(liveGamePhase);
          this.isLoading = false;
        }
      });
    },
    async fetchPlayerRole() {
      try {
        this.isLoading = true;

        let getPlayerRoleRes = await gameService.getPlayerRole2({
          playerId: this.user.id,
        });

        if (
          getPlayerRoleRes &&
          getPlayerRoleRes.status === 200 &&
          getPlayerRoleRes.data
        ) {
        const { isSaboteur, realTextId } = getPlayerRoleRes.data;

        if (isSaboteur) {
          this.gameStore.setSaboteurStatus();
          this.gameStore.setSaboteur(this.user);
        }

        if (isSaboteur || this.isModerator) {
          this.gameStore.updateRealText(realTextId);
        }

        if (!this.isModerator) {
          const shouldShowRoleButton = [
            "DISCUSS",
            "VOTE",
            "REVEAL",
            "DEBRIEF",
          ].includes(this.game?.phase);
          this.playerRoleReady = shouldShowRoleButton;
          if (shouldShowRoleButton) {
            await this.$nextTick();
            if (this.isSaboteur) {
              let retries = 0;
              const waitForLabel = async () => {
                await this.$nextTick();
                if (
                  this.gameStore.saboteurRealTextLabe ||
                  retries > 20
                ) {
                    if (this.game?.phase === "DISCUSS" && !this.hasShownRoleModal && !this.isReconnectFlow) {
                      this.showPlayerRoleModal = true;
                      this.hasShownRoleModal = true;
                    }
                    return;
                  }
                retries++;
                setTimeout(waitForLabel, 100);
              };
              waitForLabel();
            } else {
              if (this.game?.phase === "DISCUSS" && !this.hasShownRoleModal && !this.isReconnectFlow) {
                this.showPlayerRoleModal = true;
                this.hasShownRoleModal = true;
              }
            }
          }
        }
        } else {
          console.error("Error while fetching the player role");
          toast("Unable to fetch your role. Please try again!", {
            type: "error",
          });
        }
        console.log("ROLE API RESPONSE", getPlayerRoleRes.data);
      } catch (error) {
        console.error("Error while fetching the player role: ", error);
        toast(
          this.$t("unexpected-error"),
          {
            type: "error",
          },
        );
      } finally {
        this.isLoading = false;
      }
    },
    listenToPlayers(gameDocId) {
         if (this.game?.phase === "READ") {
            this.playersReady = false;
          }
      // alert("to test: ", chatDocId)
      // console.log("gameDocId in players listen: ", gameDocId);
      if (this.fbPlayersSnapshot) {
        this.fbPlayersSnapshot();
        this.fbPlayersSnapshot = null;
      }
      const playersColRef = collection(fireDB, "games", gameDocId, "players");

      // Unsubscribe if an existing listener is already active
      if (this.fbPlayersSnapshot) {
        this.fbPlayersSnapshot(); // Stop the old listener before starting a new one
      }

      this.fbPlayersSnapshot = onSnapshot(playersColRef, (snapshot) => {
        const livePlayers = [];

      const existingPlayers = this.gameStore.game?.players || [];

      snapshot.forEach((doc) => {
        const playerId = parseInt(doc.data().user_id);
        if (playerId === Number(this.game?.host?.id)) {
          return;
        }

        const existing = existingPlayers.find(ep => ep.id === playerId);

        livePlayers.push({
          docId: doc.id,
          id: playerId,
          username: doc.data().username,
          avatar: doc.data().avatar,
          joineddAt: doc.data().joined_at,
          status: "Ready",
          prelimVotedAt: doc.data().prelim_voted_at,
          votedAt: doc.data().voted_at,
          textVotedAt: doc.data().text_voted_at,
          votedTextId: doc.data().voted_text_id,
          votedSaboteurId: doc.data().voted_saboteur_id,
          saboteurVotedAt: doc.data().saboteur_voted_at,

          // ✅ FIXED
          groupId: doc.data().group_id ?? existing?.groupId ?? null,

          role:
            doc.data().role ??
            (playerId === Number(this.game?.host?.id) ? "host" : "player"),

          moderatorGroupIds:
            doc.data().moderator_group_ids ??
            doc.data().moderatorGroupIds ??
            existing?.moderatorGroupIds ??
            [],
        });
      });

        // Keep JOIN-phase preview groups deterministic by ordering players by
        // the time they joined the lobby, then by numeric id as a fallback.
        livePlayers.sort((a, b) => {
          const aJoinedAt =
            typeof a.joineddAt?.toMillis === "function"
              ? a.joineddAt.toMillis()
              : Number(a.joineddAt?.seconds ?? 0);
          const bJoinedAt =
            typeof b.joineddAt?.toMillis === "function"
              ? b.joineddAt.toMillis()
              : Number(b.joineddAt?.seconds ?? 0);

          if (aJoinedAt !== bJoinedAt) {
            return aJoinedAt - bJoinedAt;
          }

          return Number(a.id ?? 0) - Number(b.id ?? 0);
        });


        // const mergedPlayers = livePlayers.map((p) => {
        //   const existing = existingPlayers.find(
        //     (ep) => Number(ep.id) === Number(p.id)
        //   );

        //   return {
        //     ...p,
        //     role:
        //       p.role && p.role !== "player"
        //         ? p.role
        //         : existing?.role || p.role || "player",
        //     moderatorGroupId:
        //       p.moderatorGroupId !== null && p.moderatorGroupId !== undefined
        //         ? p.moderatorGroupId
        //         : existing?.moderatorGroupId ?? null,
        //   };
        // });
        const mergedPlayers = livePlayers.map((p) => ({
          ...p,
        }));

        //("✅ MERGED PLAYERS:", mergedPlayers);

        this.gameStore.updatePlayers(mergedPlayers);
        const playersIndex = {};
        for (const p of mergedPlayers) {
          const gid = Number(p.groupId);
          if (!gid) continue;
          (playersIndex[gid] ||= []).push(p.id);
        }

        this.gameGroupStore.setPlayersIndex(playersIndex);
        this.$nextTick(() => {
          if (this.game?.phase === "READ" && (this.isHost || this.isModerator)) {
            this.playersReady = true;
          } else {
            this.playersReady = true;
          }
        });
      });
    },
    listenToGroupMessages(gameDocId, groupDocId, groupId) {
      // alert("to test: ", chatDocId)
      // console.log("gameDocId in chat: ", gameDocId);
      // console.log("groupDocId in chat: ", groupDocId);
      // Avoid duplicate listeners if this method is called twice
      if (this.fbMessagesSnapshot) {
        this.fbMessagesSnapshot(); // unsubscribe old
        this.fbMessagesSnapshot = null;
      }

      const messagesColRef = collection(
        fireDB,
        "games",
        gameDocId,
        "groups",
        groupDocId,
        "chat",
      );
      // const messagesQuery = query(
      //   messagesColRef,
      //   // orderBy
      //   orderBy("created_at", "desc"), // @TODO: check and remove if needed
      //   limit(100)
      // );
      const messagesQuery = query(
        messagesColRef,
        orderBy("created_at", "asc"),
        limit(200),
      );

      this.fbMessagesSnapshot = onSnapshot(messagesQuery, (snapshot) => {
        // snapshot.docChanges().forEach((change) => {
        //   if (change.type === "added") {
        //     // console.log("New message: ", change.doc.data());
        //     let newMessage = {
        //       docId: change.doc.id,
        //       userName: change.doc.data().user_name,
        //       userAvatar: change.doc.data().user_avatar,
        //       userId: parseInt(change.doc.data().user_id),
        //       isHost: change.doc.data().is_host,
        //       createdAt: change.doc.data().created_at,
        //       text: change.doc.data().text,
        //     };

        //     // this.gameStore.addMessage(newMessage);

        //     this.gameGroupStore.appendMessage(groupId, newMessage);
        //   }
        //   if (change.type === "modified") {
        //     // console.log("Modified message: ", change.doc.data());
        //   }
        //   if (change.type === "removed") {
        //     // console.log("Removed message: ", change.doc.data());
        //   }
        // });

        const batch = [];

        snapshot.docChanges().forEach((change) => {
          if (change.type !== "removed") {
            const d = change.doc;
            const data = d.data();

            batch.push({
              docId: d.id,
              userName: data.user_name,
              userAvatar: data.user_avatar,
              userId: parseInt(data.user_id),
              isHost: data.is_host,
              isModerator: data.is_moderator ?? false,
              createdAt: data.created_at,
              text: data.text,
              textClean: this.badWordsFilter?.clean?.(data.text) ?? data.text,
            });
          }
        });

        if (batch.length) {
          this.gameGroupStore.appendMessageBatch(groupId, batch);
        }
      });
    },
    listenToAllMessages(gameDocId) {
      const existing = new Set(Object.keys(this.fbAllChatsUnsubs));

      const desired = new Map();

      const sourceGroups = this.isModerator
        ? this.scopedGroups
        : this.groups;

      for (const g of sourceGroups) {
        if (!g?.fbDocId) continue;

        desired.set(g.fbDocId, {
          groupId: Number(g.id),
          fbGroupDocId: g.fbDocId,
        });
      }

      for (const fbId of existing) {
        if (!desired.has(fbId)) {
          this.fbAllChatsUnsubs[fbId]?.();
          delete this.fbAllChatsUnsubs[fbId];
        }
      }

      const badFilter = this.badWordsFilter;

      for (const { groupId, fbGroupDocId } of desired.values()) {
        if (this.fbAllChatsUnsubs[fbGroupDocId]) continue;

        const chatColRef = collection(
          fireDB,
          "games",
          gameDocId,
          "groups",
          fbGroupDocId,
          "chat",
        );

        const q = query(
          chatColRef,
          orderBy("created_at", "asc"),
          limit(200),
        );

        const unsub = onSnapshot(q, (snap) => {
          const batch = [];

          snap.docChanges().forEach((change) => {
            if (change.type === "removed") return;

            const d = change.doc;
            const data = d.data();

              batch.push({
                docId: d.id,
                userName: data.user_name,
                userAvatar: data.user_avatar,
                userId: parseInt(data.user_id),
                isHost: data.is_host,
                isModerator: data.is_moderator ?? false,
                createdAt: data.created_at,
                text: data.text,
                textClean: badFilter?.clean?.(data.text) ?? data.text,
              });

              // this.gameGroupStore.appendMessage(groupId, {
              //   docId: d.id,
              //   userName: data.user_name,
              //   userAvatar: data.user_avatar,
              //   userId: parseInt(data.user_id),
              //   isHost: data.is_host,
              //   createdAt: data.created_at,
              //   text: data.text,
              //   textClean: this.badWordsFilter?.clean?.(data.text) ?? data.text,
              // });

              // If you ever support edits/deletions, handle here:
              // if (change.type === "modified") { /* update in-place if needed */ }
              // if (change.type === "removed") { /* remove from array if needed */ }
            },
            {
              // Avoid metadata-only churn (e.g., pending writes, fromCache→server)
              includeMetadataChanges: false,
            },
          );
          if (batch.length) {
            this.gameGroupStore.appendMessageBatch(groupId, batch);
          }
        });

        this.fbAllChatsUnsubs[fbGroupDocId] = unsub;
      }
    },
    closeMissingVotesModal() {
      this.showMissingVotesModal = false;
    },
    closeStartGameConfirmationModal() {
      this.showStartGameConfirmationModal = false;
    },
    closeFullVotesReceivedModal() {
      this.showFullVotesReceivedModal = false;
    },
    openFullVotesReceivedModal() {
      this.showFullVotesReceivedModal = true;
    },
    closeConsensusModal() {
      this.showConsensusModal = false;
    },
    openConsensusModal() {
      this.showConsensusModal = true;
    },
    proceedWithConsensus() {
      this.showConsensusModal = false;
      this.continueGame();
    },
  },
};
</script>

